import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Pagination,
  Tooltip,
  Checkbox,
  Slider,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  FileDownload as FileDownloadIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  VolumeUp as VolumeUpIcon,
  VolumeDown as VolumeDownIcon,
  VolumeOff as VolumeOffIcon
} from '@mui/icons-material';
import { ResourceUpload } from '../components/ResourceUpload';
import ResourceViewer from '../components/ResourceViewer';
import ResourceDownload from '../components/ResourceDownload';
import { AudioPlayerDialog } from '../components/AudioPlayerDialog';
import { resourceService } from '../services/resourceService';
import { resourceTopicService } from '../services/resourceTopicService';
import { useAppStore } from '../stores/useAppStore';
import { isStudent, canUploadResources, canManageResources } from '../utils/rbac';
import { useSearchParams } from 'react-router-dom';
import type { Resource, ResourceListParams, Category } from '../types';
import { useDebounce } from '../hooks/usePerformance';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { vi } from 'date-fns/locale';
import { logger } from '../utils/logger';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from '../components/ToastNotification';
import { ExportDialog } from '../components/ExportDialog';
import { type ExportColumn } from '../services/exportService';
import { useNavigate } from 'react-router-dom';

interface ResourceManagementPageProps {
  /** Optional fixed category ID - when set, the page will only show resources from this category */
  fixedCategoryId?: string;
  /** Optional fixed category name - displayed as title when fixedCategoryId is set */
  fixedCategoryName?: string;
  /** Optional page title override */
  pageTitle?: string;
  /** Optional category name to exclude - resources with this category will be hidden */
  excludeCategoryName?: string;
  /** If true, hides the category filter chip to prevent users from clearing the fixed category */
  hideCategoryFilter?: boolean;
  /** If true, hides all tag/category chips on resource items */
  hideChips?: boolean;
}

export const ResourceManagementPage: React.FC<ResourceManagementPageProps> = ({
  fixedCategoryId,
  fixedCategoryName,
  pageTitle,
  excludeCategoryName,
  hideCategoryFilter = false,
  hideChips = false
}) => {
  const { user } = useAppStore();
  const navigate = useNavigate();
  const userRoles = user?.roles || [];
  const isStudentRole = isStudent(userRoles);
  const canUpload = canUploadResources(userRoles);
  // Teachers can only edit their own resources, Admin/Approver/Librarian can edit all
  const canEditAll = canManageResources(userRoles);
  const canExport = !isStudentRole && canManageResources(userRoles);
  const [searchParams, setSearchParams] = useSearchParams();

  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState<string | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingResource, setViewingResource] = useState<Resource | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editTagInput, setEditTagInput] = useState('');
  const [editSelectedCategories, setEditSelectedCategories] = useState<Category[]>([]);
  const [editCategories, setEditCategories] = useState<Category[]>([]);
  const [loadingEditCategories, setLoadingEditCategories] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [resourceInfoDialogOpen, setResourceInfoDialogOpen] = useState(false);
  const [resourceForInfo, setResourceForInfo] = useState<Resource | null>(null);
  const [audioPlayerOpen, setAudioPlayerOpen] = useState(false);
  const [audioResource, setAudioResource] = useState<Resource | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<string>(''); // Filter by tag
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(fixedCategoryId || ''); // Filter by category
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>(fixedCategoryName || ''); // Category name for display
  const [excludeCategoryId, setExcludeCategoryId] = useState<string>(''); // Category ID to exclude

  const { snackbar, showSuccess, showError, showInfo, hideSnackbar } = useSnackbar();

  // Lookup excluded category ID on mount
  useEffect(() => {
    const lookupExcludeCategoryId = async () => {
      if (!excludeCategoryName) return;

      try {
        const response = await resourceTopicService.getTopics({ page: 1, pageSize: 1000 });
        const topics = response.categories || [];
        const excludeTopic = topics.find((t: any) =>
          t.name.toLowerCase() === excludeCategoryName.toLowerCase()
        );
        if (excludeTopic) {
          setExcludeCategoryId(excludeTopic.id);
        }
      } catch (err) {
        console.error('Error looking up exclude category:', err);
      }
    };

    lookupExcludeCategoryId();
  }, [excludeCategoryName]);

  const handleViewResource = (resource: Resource) => {
    setViewingResource(resource);
    setViewerOpen(true);
  };

  const handleResourceTitleClick = async (resource: Resource) => {
    const fileTypeLower = resource.fileType.toLowerCase();
    const normalizedType = fileTypeLower.startsWith('.')
      ? fileTypeLower
      : `.${fileTypeLower}`;

    // Check if it's an audio file (mp3, etc.)
    const isAudio = ['.mp3', '.wav', '.flac', '.aac', '.ogg'].includes(normalizedType);

    // Check if it's an office file
    const isOfficeDoc = ['.docx', '.doc', '.xlsx', '.xls', '.pptx', '.ppt'].includes(normalizedType);

    // Check if it's an archive file
    const isArchive = ['.zip', '.rar', '.7z', '.tar', '.gz'].includes(normalizedType);

    if (isAudio) {
      // For audio files, open audio player dialog
      try {
        const blob = await resourceService.getResourceFile(resource.id);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setAudioResource(resource);
        setAudioPlayerOpen(true);
        // Increment view count
        await resourceService.viewResource(resource.id);
      } catch (err) {
        showError('Không thể tải audio');
      }
    } else if (isOfficeDoc) {
      // For office files, use ResourceViewer (which checks for Office Online Viewer plugin)
      handleViewResource(resource);
    } else if (isArchive) {
      // For archive files, show info dialog
      setResourceForInfo(resource);
      setResourceInfoDialogOpen(true);
    } else {
      // For other viewable files (PDF, images, videos), use ResourceViewer
      handleViewResource(resource);
    }
  };

  const handleTagClick = (tag: string) => {
    setSelectedTag(tag);
    setPage(1);
  };

  const handleCategoryClick = (categoryId: string, categoryName?: string) => {
    setSelectedCategoryId(categoryId);
    setSelectedCategoryName(categoryName || '');
    setPage(1);
  };

  const handleClearTagFilter = () => {
    setSelectedTag('');
    setPage(1);
  };

  const handleClearCategoryFilter = () => {
    setSelectedCategoryId('');
    setSelectedCategoryName('');
    setPage(1);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setViewingResource(null);
    // Refresh resources list to update viewCount and downloadCount after a short delay
    // to ensure backend has updated the counts
    // Trigger refresh by updating refreshTrigger state, which will cause useEffect to call loadResources()
    setTimeout(() => {
      setRefreshTrigger(prev => prev + 1);
    }, 300);
  };

  const handleCloseAudioPlayer = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setAudioPlayerOpen(false);
    setAudioResource(null);
  };

  const handleDownloadResource = async (resource: Resource) => {
    try {
      const blob = await resourceService.downloadResource(resource.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resource.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showSuccess('Đã tải xuống tệp!');
    } catch (err: any) {
      showError(err?.message || 'Không thể tải xuống tệp');
    }
  };

  const handleCopyLink = async (resource: Resource) => {
    try {
      // Generate share token first
      const tokenData = await resourceService.getShareToken(resource.id);
      const token = tokenData.token;

      // Create shareable public link with token
      const shareableLink = `${window.location.origin}/public/resource/${resource.id}?token=${encodeURIComponent(token)}`;

      // Try modern Clipboard API first (requires HTTPS or localhost)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(shareableLink);
          showSuccess('Đã sao chép link!');
          return;
        } catch (clipboardError) {
          logger.warn('Clipboard API failed, trying fallback:', clipboardError);
          // Fall through to fallback method
        }
      }

      // Fallback method for environments without Clipboard API (HTTP, older browsers)
      const textArea = document.createElement('textarea');
      textArea.value = shareableLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          showSuccess('Đã sao chép link!');
        } else {
          throw new Error('execCommand failed');
        }
      } catch (fallbackError) {
        logger.error('Fallback copy method failed:', fallbackError);
        // Last resort: show the link in a prompt
        const userConfirmed = window.prompt('Không thể tự động sao chép. Vui lòng sao chép link sau:', shareableLink);
        if (userConfirmed) {
          showSuccess('Đã sao chép link!');
        } else {
          showError('Không thể sao chép link');
        }
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (err) {
      logger.error('Error copying link:', err);
      showError('Không thể sao chép link');
    }
  };


  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Convert dates to strings for debouncing
  const startDateStr = startDate
    ? `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`
    : '';
  const endDateStr = endDate
    ? `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`
    : '';

  // Debounce dates
  const debouncedStartDateStr = useDebounce(startDateStr, 800);
  const debouncedEndDateStr = useDebounce(endDateStr, 800);

  const loadResources = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ResourceListParams = {
        page,
        pageSize,
        searchTerm: debouncedSearchTerm || undefined,
        startDate: debouncedStartDateStr || undefined,
        endDate: debouncedEndDateStr || undefined,
        tag: selectedTag || undefined,
        categoryId: selectedCategoryId || undefined,
        excludeCategoryId: excludeCategoryId || undefined
      };

      logger.log('🌐 Frontend - Calling API: /api/resources', params);
      const response = await resourceService.getResources(params);
      logger.log('✅ Frontend - Received resources:', response.resources.length, 'Total:', response.totalCount);
      logger.log('📋 Frontend - Resources:', response.resources.map(r => ({ id: r.id, title: r.title, fileType: r.fileType, tags: r.tags, uploadedBy: r.uploadedByUser?.fullName })));

      // Server now handles excludeCategoryId filtering for proper pagination
      setResources(response.resources);
      setTotalCount(response.totalCount);
    } catch (err) {
      logger.error('❌ Frontend - Error loading resources:', err);
      const errorMessage = err instanceof Error ? err.message : 'Có lỗi xảy ra khi tải danh sách tài nguyên';
      setError(errorMessage);

      // If super-admin is missing schoolCode, redirect to admin tenants page
      if (user?.isSuperAdmin && errorMessage.includes('Missing x-school-code')) {
        showError('Vui lòng chọn trường học trước khi truy cập trang này. Đang chuyển đến trang Quản lý trường học...');
        setTimeout(() => {
          navigate('/admin/tenants');
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearchTerm, debouncedStartDateStr, debouncedEndDateStr, selectedTag, selectedCategoryId, excludeCategoryId, user, navigate, showError]);

  // Reset page when search or filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, debouncedStartDateStr, debouncedEndDateStr, selectedTag, selectedCategoryId]);

  useEffect(() => {
    loadResources();
  }, [loadResources, refreshTrigger]);

  // Auto-open resource viewer if resourceId is in URL (for shareable links)
  useEffect(() => {
    const resourceId = searchParams.get('resourceId');
    if (resourceId && resources.length > 0) {
      const resource = resources.find(r => r.id === resourceId);
      if (resource) {
        setViewingResource(resource);
        setViewerOpen(true);
        // Remove resourceId from URL after opening
        searchParams.delete('resourceId');
        setSearchParams(searchParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resources, searchParams, setSearchParams]);


  const handleClearFilters = () => {
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
    setSelectedTag('');
    setSelectedCategoryId('');
    setSelectedCategoryName('');
    setPage(1);
  };

  const handleRefresh = () => {
    // Clear all filters and reload
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
    setPage(1);
    // Force reload immediately
    loadResources();
  };

  const handleUploadSuccess = () => {
    setUploadDialogOpen(false);
    setRefreshTrigger(prev => prev + 1);
    showSuccess('Tải lên tài nguyên thành công!');
    loadResources();
  };

  const handleUploadError = (error: string) => {
    showError(error);
  };

  const handleDeleteResource = async (id: string) => {
    setResourceToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteResource = async () => {
    if (!resourceToDelete) return;

    try {
      await resourceService.deleteResource(resourceToDelete);
      showSuccess('Xóa tài nguyên thành công!');
      loadResources();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi xóa tài nguyên');
    } finally {
      setDeleteDialogOpen(false);
      setResourceToDelete(null);
    }
  };

  const handleBulkDelete = () => {
    if (selectedResources.length === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedResources.length === 0) return;

    const count = selectedResources.length;
    const idsToDelete = [...selectedResources];

    try {
      await resourceService.deleteMultipleResources(idsToDelete);
      setSelectedResources([]);
      setBulkDeleteDialogOpen(false);
      showSuccess(`Đã xóa ${count} tài nguyên thành công!`);
      loadResources();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi xóa tài nguyên');
      setBulkDeleteDialogOpen(false);
    }
  };

  const handleSelectResource = (id: string) => {
    setSelectedResources(prev =>
      prev.includes(id)
        ? prev.filter(resourceId => resourceId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedResources(resources.map(r => r.id));
    } else {
      setSelectedResources([]);
    }
  };

  const handleEditResource = async (resource: Resource) => {
    setEditingResource(resource);
    setEditTitle(resource.title);
    setEditDescription(resource.description || '');
    setEditTags(resource.tags || []);
    setEditTagInput('');

    // Load current categories (topics) of the resource
    if (resource.categories && resource.categories.length > 0) {
      setEditSelectedCategories(resource.categories);
    } else {
      setEditSelectedCategories([]);
    }

    // Load all available categories (topics)
    await loadEditCategories();

    setEditDialogOpen(true);
  };

  const loadEditCategories = async () => {
    try {
      setLoadingEditCategories(true);
      const response = await resourceTopicService.getTopics({ page: 1, pageSize: 1000 });
      setEditCategories(response.categories || []);
    } catch (err) {
      console.error('Error loading resource topics:', err);
    } finally {
      setLoadingEditCategories(false);
    }
  };

  const handleEditTagAdd = () => {
    if (editTagInput.trim() && !editTags.includes(editTagInput.trim())) {
      setEditTags(prev => [...prev, editTagInput.trim()]);
      setEditTagInput('');
    }
  };

  const handleEditTagRemove = (tagToRemove: string) => {
    setEditTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const handleEditTagKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleEditTagAdd();
    }
  };

  const confirmEditResource = async () => {
    if (!editingResource) return;

    try {
      await resourceService.updateResource(editingResource.id, {
        title: editTitle.trim(),
        description: editDescription.trim() || undefined,
        tags: editTags,
        categoryIds: editSelectedCategories.map(c => c.id)
      });
      showSuccess('Cập nhật tài nguyên thành công!');
      setEditDialogOpen(false);
      setEditingResource(null);
      setEditSelectedCategories([]);
      loadResources();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi cập nhật tài nguyên');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const formatFileSize = (bytes: number) => {
    return resourceService.formatFileSize(bytes);
  };

  const getFileTypeIcon = (fileType: string) => {
    return resourceService.getFileTypeIcon(fileType);
  };

  const isViewableFile = (fileType: string): boolean => {
    if (!fileType) return false;
    // Normalize file type: ensure it starts with dot, convert to lowercase
    const normalizedType = fileType.startsWith('.')
      ? fileType.toLowerCase()
      : `.${fileType.toLowerCase()}`;
    const fileTypeLower = normalizedType.toLowerCase();

    // Explicitly exclude office documents and archives first
    const isOfficeDoc = ['.docx', '.doc', '.xlsx', '.xls', '.pptx', '.ppt'].includes(fileTypeLower);
    const isArchive = ['.zip', '.rar', '.7z', '.tar', '.gz'].includes(fileTypeLower);

    if (isOfficeDoc || isArchive) {
      return false;
    }

    // Only allow viewable file types
    const isImage = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'].includes(fileTypeLower);
    const isPDF = fileTypeLower === '.pdf';
    const isVideo = ['.mp4', '.avi', '.mov', '.wmv', '.webm'].includes(fileTypeLower);
    const isAudio = ['.mp3', '.wav', '.flac', '.aac', '.ogg'].includes(fileTypeLower);

    return isPDF || isImage || isVideo || isAudio;
  };

  const handleExportResources = async (dateFrom: Date | null, dateTo: Date | null): Promise<Resource[]> => {
    return await resourceService.exportResources(dateFrom, dateTo);
  };

  const resourceExportColumns: ExportColumn[] = [
    { key: 'title', label: 'Tên tài nguyên', width: 30 },
    { key: 'description', label: 'Mô tả', width: 40 },
    { key: 'fileName', label: 'Tên file', width: 25 },
    { key: 'fileType', label: 'Loại file', width: 12 },
    { key: 'fileSize', label: 'Kích thước', width: 15 },
    { key: 'uploadedByUser.fullName', label: 'Người tải lên', width: 20 },
    { key: 'uploadDate', label: 'Ngày tải lên', width: 15 },
    { key: 'viewCount', label: 'Lượt xem', width: 12 },
    { key: 'downloadCount', label: 'Lượt tải', width: 12 }
  ];

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3, gap: 2 }}>
        {canExport && (
          <Tooltip title="Xuất Excel" arrow>
            <IconButton
              color="primary"
              onClick={() => setExportDialogOpen(true)}
              aria-label="Xuất Excel"
              size="small"
            >
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canUpload && !isStudentRole && (
          <Tooltip title="Tải lên sách số" arrow>
            <IconButton
              color="primary"
              onClick={() => setUploadDialogOpen(true)}
              aria-label="Tải lên sách số"
              size="small"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Tìm kiếm tổng hợp (Tên, loại file, tags, người tải lên)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && setDebouncedSearchTerm(searchTerm)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    )
                  }}
                />
              </Box>
              <DatePicker
                label="Từ ngày"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{ textField: { size: 'small', sx: { width: '180px' } } }}
              />
              <DatePicker
                label="Đến ngày"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { size: 'small', sx: { width: '180px' } } }}
              />
              <Tooltip title="Làm mới" arrow>
                <IconButton
                  color="primary"
                  onClick={handleRefresh}
                  aria-label="Làm mới"
                  size="small"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {(selectedTag || selectedCategoryId) && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {selectedTag && (
                    <Chip
                      label={`Tag: ${selectedTag}`}
                      onDelete={handleClearTagFilter}
                      color="primary"
                      size="small"
                    />
                  )}
                  {selectedCategoryId && !hideCategoryFilter && (
                    <Chip
                      label={`Chủ đề: ${selectedCategoryName || selectedCategoryId}`}
                      onDelete={handleClearCategoryFilter}
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
              )}
              {selectedResources.length > 0 && (
                <Tooltip title={`Xóa (${selectedResources.length})`} arrow>
                  <IconButton
                    color="error"
                    onClick={handleBulkDelete}
                    aria-label="Xóa"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Resources Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper} sx={{ minHeight: 200, width: '100%' }}>
            <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '30%' }}>Sách số ({totalCount})</TableCell>
                  <TableCell sx={{ width: '10%' }}>Thông tin</TableCell>
                  <TableCell sx={{ width: '18%' }}>Người Tải Lên</TableCell>
                  <TableCell sx={{ width: '10%' }}>Xem | Tải</TableCell>
                  <TableCell sx={{ width: '18%' }}>Thao Tác</TableCell>
                  <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                    {canManageResources(userRoles) && (
                      <Checkbox
                        indeterminate={selectedResources.length > 0 && selectedResources.length < resources.length}
                        checked={resources.length > 0 && selectedResources.length === resources.length}
                        onChange={handleSelectAll}
                        size="small"
                        sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : resources.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Không có tài nguyên nào
                    </TableCell>
                  </TableRow>
                ) : (
                  resources.map((resource) => {
                    const isSelected = selectedResources.includes(resource.id);
                    // Check if user is the uploader
                    const isUploader = resource.uploadedByUserId && user?.id && resource.uploadedByUserId === user.id;
                    // Check if user can delete this resource
                    // User can delete if: 
                    // 1. They have upload permission AND they are the uploader (can delete their own resources)
                    // 2. OR they are Admin/Approver/Librarian (can delete any resource)
                    const canDelete = (canUpload && isUploader) || canManageResources(userRoles);
                    // Check if user can edit this resource
                    // Teachers can only edit their own resources, Admin/Approver/Librarian can edit all
                    const canEdit = isUploader || canEditAll;

                    return (
                      <TableRow key={resource.id}>
                        <TableCell>
                          <Box>
                            <Tooltip
                              title={
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">{resource.title}</Typography>
                                  {resource.description && (
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                      {resource.description}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              arrow
                            >
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                onClick={() => handleResourceTitleClick(resource)}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': { textDecoration: 'underline' },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '100%'
                                }}
                              >
                                {resource.title}
                              </Typography>
                            </Tooltip>
                            {resource.aiStatus && resource.aiStatus !== 'COMPLETED' && (
                              <Box sx={{ mt: 0.5 }}>
                                <Chip
                                  label={resource.aiStatus === 'PROCESSING' ? '✨ AI đang xử lý...' : resource.aiStatus === 'FAILED' ? '❌ AI lỗi' : '⏳ Chờ AI...'}
                                  size="small"
                                  variant="outlined"
                                  color={resource.aiStatus === 'PROCESSING' ? 'primary' : resource.aiStatus === 'FAILED' ? 'error' : 'default'}
                                  sx={{ height: '20px', fontSize: '0.7rem' }}
                                />
                              </Box>
                            )}
                            {!hideChips && ((resource.categories && resource.categories.length > 0) || (resource.tags && resource.tags.length > 0)) ? (
                              <Box
                                sx={{
                                  mt: 0.5,
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.5,
                                  maxHeight: '24px',
                                  overflow: 'hidden'
                                }}
                              >
                                {resource.categories && resource.categories.map((category) => (
                                  <Chip
                                    key={category.id}
                                    label={category.name}
                                    size="small"
                                    color="primary"
                                    onClick={() => handleCategoryClick(category.id, category.name)}
                                    sx={{
                                      mr: 0.5,
                                      mb: 0,
                                      cursor: 'pointer',
                                      height: '20px',
                                      fontSize: '0.7rem',
                                      '& .MuiChip-label': {
                                        padding: '0 6px'
                                      }
                                    }}
                                  />
                                ))}
                                {resource.tags && resource.tags.map((tag, index) => (
                                  <Chip
                                    key={index}
                                    label={tag}
                                    size="small"
                                    onClick={() => handleTagClick(tag)}
                                    sx={{
                                      mr: 0.5,
                                      mb: 0,
                                      cursor: 'pointer',
                                      height: '20px',
                                      fontSize: '0.7rem',
                                      '& .MuiChip-label': {
                                        padding: '0 6px'
                                      }
                                    }}
                                  />
                                ))}
                              </Box>
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <span>{getFileTypeIcon(resource.fileType)}</span>
                              <Typography variant="body2" fontWeight="medium">
                                {resource.fileType.toUpperCase()}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {formatFileSize(resource.fileSize)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {resource.uploadedByUser?.fullName || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatDate(resource.uploadDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {resource.viewCount || 0} | {resource.downloadCount || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <ResourceDownload
                              resource={resource}
                              variant="icon"
                              size="small"
                              onDownloadComplete={() => {
                                showSuccess('Đã tải xuống tệp!');
                                // Refresh resources list to update downloadCount
                                // Trigger refresh by updating refreshTrigger state
                                setTimeout(() => {
                                  setRefreshTrigger(prev => prev + 1);
                                }, 300);
                              }}
                              onDownloadError={(e) => showError(e)}
                            />
                            <Tooltip title="Sao chép link">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyLink(resource)}
                              >
                                <CopyIcon />
                              </IconButton>
                            </Tooltip>
                            {canEdit && (
                              <Tooltip title="Chỉnh sửa">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditResource(resource)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Xóa">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteResource(resource.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                          {canDelete && (
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectResource(resource.id)}
                              size="small"
                              sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Tải Lên Sách Số Mới</DialogTitle>
        <DialogContent>
          <ResourceUpload onUploadSuccess={handleUploadSuccess} onUploadError={handleUploadError} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>
            Đóng
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resource Viewer Dialog */}
      {viewingResource && (
        <ResourceViewer
          resource={viewingResource}
          open={viewerOpen}
          onClose={handleCloseViewer}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa tài nguyên này? Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Hủy
          </Button>
          <Button onClick={confirmDeleteResource} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)}>
        <DialogTitle>Xác nhận xóa nhiều tài nguyên</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa {selectedResources.length} tài nguyên đã chọn?
            Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>
            Hủy
          </Button>
          <Button onClick={confirmBulkDelete} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Resource Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingResource(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Chỉnh sửa tài nguyên</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Tiêu đề *"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              required
              helperText="Tiêu đề mô tả nội dung tài nguyên"
            />
            <TextField
              fullWidth
              label="Mô tả"
              multiline
              rows={3}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              helperText="Mô tả chi tiết về tài nguyên (tùy chọn)"
            />
            <Autocomplete
              multiple
              options={editCategories}
              getOptionLabel={(option) => option.name}
              value={editSelectedCategories}
              onChange={(event, newValue) => {
                setEditSelectedCategories(newValue);
              }}
              loading={loadingEditCategories}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Chủ đề"
                  placeholder="Chọn chủ đề (có thể chọn nhiều)"
                  helperText="Chọn một hoặc nhiều chủ đề cho tài nguyên này"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Thẻ (Tags)
              </Typography>
              <Box display="flex" gap={1} mb={1}>
                <TextField
                  placeholder="Nhập thẻ và nhấn Enter"
                  value={editTagInput}
                  onChange={(e) => setEditTagInput(e.target.value)}
                  onKeyPress={handleEditTagKeyPress}
                  size="small"
                  sx={{ flexGrow: 1 }}
                />
                <Button onClick={handleEditTagAdd} variant="outlined" size="small">
                  Thêm
                </Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {editTags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => handleEditTagRemove(tag)}
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setEditingResource(null);
            setEditSelectedCategories([]);
          }}>
            Hủy
          </Button>
          <Button
            onClick={confirmEditResource}
            variant="contained"
            disabled={!editTitle.trim()}
          >
            Lưu
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resource Detail Dialog - Similar to User Management */}
      <Dialog open={resourceInfoDialogOpen} onClose={() => setResourceInfoDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" component="div">
            {resourceForInfo?.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {resourceForInfo && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3, mt: 1 }}>
              <Box>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Thông tin cơ bản
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Tên tệp
                      </Typography>
                      <Typography variant="body1">{resourceForInfo.title}</Typography>
                    </Box>
                    {resourceForInfo.description && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Mô tả
                        </Typography>
                        <Typography variant="body1">{resourceForInfo.description}</Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Loại tệp
                      </Typography>
                      <Typography variant="body1">{resourceForInfo.fileType.toUpperCase()}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Kích thước
                      </Typography>
                      <Typography variant="body1">{formatFileSize(resourceForInfo.fileSize)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Người tải lên
                      </Typography>
                      <Typography variant="body1">{resourceForInfo.uploadedByUser?.fullName || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Ngày tải lên
                      </Typography>
                      <Typography variant="body1">{formatDate(resourceForInfo.uploadDate)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Lượt xem
                      </Typography>
                      <Typography variant="body1">{resourceForInfo.viewCount || 0}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Lượt tải
                      </Typography>
                      <Typography variant="body1">{resourceForInfo.downloadCount || 0}</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
              <Box>
                {resourceForInfo.categories && resourceForInfo.categories.length > 0 && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Chủ đề
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {resourceForInfo.categories.map((category) => (
                        <Chip
                          key={category.id}
                          label={category.name}
                          size="small"
                          color="primary"
                          onClick={() => {
                            handleCategoryClick(category.id, category.name);
                            setResourceInfoDialogOpen(false);
                          }}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Paper>
                )}
                {resourceForInfo.tags && resourceForInfo.tags.length > 0 && (
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Thẻ
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {resourceForInfo.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="small"
                          onClick={() => {
                            handleTagClick(tag);
                            setResourceInfoDialogOpen(false);
                          }}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Paper>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResourceInfoDialogOpen(false)}>Đóng</Button>
        </DialogActions>
      </Dialog>

      {/* Audio Player Dialog */}
      <AudioPlayerDialog
        open={audioPlayerOpen}
        onClose={handleCloseAudioPlayer}
        audioUrl={audioUrl}
        resource={audioResource}
      />

      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onExport={handleExportResources}
        columns={resourceExportColumns}
        fileName="Danh_sach_tai_nguyen"
        sheetName="Tài nguyên"
        title="Xuất danh sách tài nguyên ra Excel"
        description="Chọn khoảng thời gian để xuất dữ liệu tài nguyên. Dữ liệu sẽ được xuất theo ngày tải lên."
        requireDateRange={true}
      />

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Box>
  );
};
